import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { LogIn, Eye, EyeOff, Sparkles } from "lucide-react";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please enter email and password");
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome to Prepify!");
      navigate("/app");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel - Modern Gradient */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-prepify-600 via-prepify-700 to-prepify-900 items-center justify-center p-12 relative overflow-hidden">
        {/* Background Decoration */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-prepify-500/30 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-md text-white relative z-10">
          <div className="flex items-center gap-2 mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_client-order-dash/artifacts/0yz21vmt_Picture%201.png" 
              alt="Prepify" 
              className="h-10 brightness-0 invert"
            />
          </div>
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Internal Management <br/>System
          </h1>
          <p className="text-prepify-100 text-lg leading-relaxed">
            Streamline your workflow. Manage clients, orders, tasks, and team attendance — all in one powerful platform.
          </p>
          
          {/* Feature pills */}
          <div className="mt-8 flex flex-wrap gap-3">
            {["Orders", "Attendance", "Tasks", "Reports"].map((feature) => (
              <span 
                key={feature}
                className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium border border-white/20"
              >
                {feature}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Clean White */}
      <div className="flex-1 flex items-center justify-center p-8 bg-gradient-to-br from-slate-50 to-white">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8 flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_client-order-dash/artifacts/0yz21vmt_Picture%201.png" 
              alt="Prepify" 
              className="h-9"
            />
          </div>

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={20} className="text-prepify-600" />
              <span className="text-sm font-medium text-prepify-600">Prepify Internal OS</span>
            </div>
            <h2 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Welcome back
            </h2>
            <p className="text-slate-500 mt-2">
              Sign in to your account to continue
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@prepify.com"
                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-prepify-500/20 focus:border-prepify-500 transition-all duration-200"
                data-testid="login-email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-prepify-500/20 focus:border-prepify-500 transition-all duration-200 pr-12"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-prepify-500 to-prepify-700 text-white font-medium rounded-lg hover:from-prepify-600 hover:to-prepify-800 focus:outline-none focus:ring-2 focus:ring-prepify-500/50 transition-all duration-200 shadow-lg shadow-prepify-500/25 active:scale-[0.98]"
              data-testid="login-submit"
            >
              {loading ? (
                <div className="spinner" style={{ width: 20, height: 20, borderTopColor: 'white' }}></div>
              ) : (
                <>
                  <LogIn size={18} />
                  Sign In
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <p className="mt-8 text-center text-sm text-slate-400">
            Need access? Contact your administrator
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
